<template>
  <StepPanel>
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-4">
        <div class="flex flex-row items-center justify-between">
          <UiText
            as="p"
            class="font-normal font-archivo text-primary tracking-10p"
            :text="listingType === 'freelancer' ? 'CREATE FREELANCER LISTING' : (listingType === 'app_service_provider' ? 'CREATE APP SERVICE PARTNER LISTING' : 'CREATE AGENCY LISTING')"
          />
          <UiText as="p" class="font-normal font-archivo text-primary tracking-10p" text="2/3" />
        </div>
        <UiText as="h3" class="font-normal text-primary" text="Professional Details" />
        <UiText
          as="p"
          class="text-primary"
          text="Select your main role and the primary services you want to offer. Also, select the minimum project budget you'd be happy with."
        />
      </div>

      <div class="flex flex-col gap-8">
        <UiFormBuilder ref="formBuilder" :schema="professionalDetailsSchema" :rows="rows" :initialValues="initialValues" scroll-to-first-error v-model="formData" @submit="goToNext">
          <template #actions>
            <div class="flex justify-between gap-4">
              <UiButton
                  variant="outline"
                  label="Back"
                  @click="goToPrevious"
              />
              <UiButton
                  variant="primary"
                  label="Next"
                  type="submit"
              />
            </div>
          </template>
        </UiFormBuilder>
      </div>
    </div>
  </StepPanel>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { UiFormBuilder, UiButton, UiText } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import StepPanel from "./StepPanel.vue";
import { freelancerDetailsFormSchema, agencyDetailsFormSchema } from "./schemas/professionalDetailsForm";
import { shopifyRoleOptions } from '@/utils/roles';

const router = useRouter()

const storedType = localStorage.getItem('listingType')
const listingType = ref<'freelancer' | 'agency' | 'app_service_provider'>(
  (storedType === 'freelancer' || storedType === 'agency' || storedType === 'app_service_provider') ? storedType : 'freelancer'
)
const isAgencyFlow = computed(() => listingType.value === 'agency' || listingType.value === 'app_service_provider')

const professionalDetailsSchema = computed(() =>
  listingType.value === 'freelancer' ? freelancerDetailsFormSchema : agencyDetailsFormSchema
)

const formData = ref<any>(
  listingType.value === 'freelancer'
    ? { service: '', role: '', budget: 0 }
    : { service: '', budget: 0 }
)

const partnerTierOptions = [
  { value: 'not-partner', label: 'Not a Partner' },
  { value: 'registered', label: 'Registered' },
  { value: 'select', label: 'Select' },
  { value: 'plus', label: 'Plus' },
  { value: 'premier', label: 'Premier' },
  { value: 'platinum', label: 'Platinum' }
]

const mainServiceOptions = [
  { value: 'store-setup-and-management', label: 'Store Setup & Management' },
  { value: 'development-and-troubleshooting', label: 'Development & Troubleshooting' },
  { value: 'marketing-and-sales', label: 'Marketing & Sales' },
  { value: 'branding-and-design', label: 'Branding & Design' },
  { value: 'copywriting-and-content', label: 'Copywriting & Content' },
  { value: 'consulting-and-strategy', label: 'Consulting & Strategy' },
  { value: 'accounting-and-financial-services', label: 'Accounting & Financial Services' },
  { value: 'ai-and-tech-solutions', label: 'AI & Tech Solutions' },
  { value: 'operations-and-management', label: 'Operations & Management' },
  { value: 'technical-support-and-maintenance', label: 'Technical Support & Maintenance' },
  { value: 'training-and-support', label: 'Training & Support' }
]

const rows = computed<BuilderRowConfig[]>(() => {
  if (listingType.value === 'freelancer') {
    return [
      {
        columns: [
          {
            type: 'select',
            name: 'service',
            label: 'Select the main service you would like to offer on the platform',
            labelCapitalize: false,
            hint: 'You can change this selection and add more categories later.',
            props: {
              placeholder: 'Choose your primary service',
              options: mainServiceOptions
            }
          },
        ]
      },
      {
        columns: [
          {
            type: 'combobox',
            name: 'role',
            label: 'Role',
            props: {
              placeholder: 'Select your role',
              options: shopifyRoleOptions
            }
          },
        ]
      },
      {
        columns: [
          {
            type: 'currency',
            name: 'budget',
            label: 'What is your minimum project budget for your services?',
            labelCapitalize: false,
            props: {
              placeholder: '500'
            }
          },
        ]
      },
    ];
  } else {
    const tierRow: BuilderRowConfig = listingType.value === 'app_service_provider'
      ? {
          columns: [
            {
              type: 'checkbox',
              name: 'appTier',
              label: '',
              labelCapitalize: false,
              hint: 'This controls whether we show the Built for Shopify badge on your app profile.',
              props: { label: 'This app is Built for Shopify' }
            },
          ]
        }
      : {
          columns: [
            {
              type: 'select',
              name: 'shopifyTier',
              label: 'What is Shopify partner tier of your agency?',
              labelCapitalize: false,
              hint: 'Recommended: Your partner tier helps clients understand your expertise level.',
              props: {
                placeholder: 'Select partner tier',
                options: partnerTierOptions
              }
            },
          ]
        }

    return [
      {
        columns: [
          {
            type: 'select',
            name: 'service',
            label: 'Select the main service you would like to offer on the platform',
            labelCapitalize: false,
            hint: 'You can change this selection and add more categories later.',
            props: {
              placeholder: 'Choose your primary service',
              options: mainServiceOptions
            }
          },
        ]
      },
      {
        columns: [
          {
            type: 'currency',
            name: 'budget',
            label: 'What is your minimum project budget for your services?',
            labelCapitalize: false,
            props: {
              placeholder: '500'
            }
          },
        ]
      },
      {
        columns: [
          {
            type: 'url',
            name: 'partnerLink',
            label: listingType.value === 'app_service_provider'
              ? 'App Store Link'
              : 'Partner link from the Shopify partner directory',
            labelCapitalize: false,
            hint: listingType.value === 'app_service_provider'
              ? 'Recommended: Add your Shopify App Store link so we can verify your app and badge status.'
              : 'Recommended: Adding your partner link helps us verify your agency credentials.',
            props: {
              placeholder: listingType.value === 'app_service_provider'
                ? 'https://apps.shopify.com/your-app'
                : 'https://partners.shopify.com/partners/your-agency-name'
            }
          },
        ]
      },
      tierRow,
    ];
  }
});

const initialValues = ref<any>(undefined);
const hasLoaded = ref<boolean>(false);
const formBuilder = ref<any>(null);

/**
 * Normalizes budget value to ensure it's always a number.
 * Handles edge cases where currency field might return a range object or array.
 */
const normalizeBudget = (budget: any): number => {
  if (budget === undefined || budget === null) {
    return 0
  }

  // Already a number
  if (typeof budget === 'number') {
    return budget
  }

  // Range object with min property
  if (typeof budget === 'object' && !Array.isArray(budget) && 'min' in budget) {
    const minValue = typeof budget.min === 'number' ? budget.min : parseFloat(budget.min)
    return isNaN(minValue) ? 0 : minValue
  }

  // Array - use first element
  if (Array.isArray(budget) && budget.length > 0) {
    const firstValue = typeof budget[0] === 'number' ? budget[0] : parseFloat(budget[0])
    return isNaN(firstValue) ? 0 : firstValue
  }

  // String - try to parse
  if (typeof budget === 'string') {
    const parsed = parseFloat(budget)
    return isNaN(parsed) ? 0 : parsed
  }

  return 0
}

onMounted(() => {
  if (!storedType || (storedType !== 'freelancer' && storedType !== 'agency' && storedType !== 'app_service_provider')) {
    router.push('/expert/signup')
    return
  }

  const saved = localStorage.getItem('professionalDetails')
  if (saved) {
    const savedData = JSON.parse(saved)
    initialValues.value = savedData
    formData.value = savedData
  }

  const storedErrors = localStorage.getItem('signupErrors')
  if (storedErrors) {
    const errors = JSON.parse(storedErrors)
    const mappedErrors: Record<string, string | undefined> = {}

    if (errors.service) mappedErrors.service = errors.service
    if (errors.budget) mappedErrors.budget = errors.budget

    if (listingType.value === 'freelancer') {
      if (errors.role) mappedErrors.role = errors.role
    } else if (isAgencyFlow.value) {
      if (errors.partnerLink) mappedErrors.partnerLink = errors.partnerLink
      if (listingType.value === 'app_service_provider') {
        if (errors.appTier) mappedErrors.appTier = errors.appTier
      } else if (errors.shopifyTier) {
        mappedErrors.shopifyTier = errors.shopifyTier
      }
    }

    localStorage.removeItem('signupErrors')

    setTimeout(() => {
      if (formBuilder.value && formBuilder.value.setErrors) {
        formBuilder.value.setErrors(mappedErrors)

        setTimeout(() => {
          if (formBuilder.value && formBuilder.value.clearErrors) {
            formBuilder.value.clearErrors()
          }
        }, 3000)
      }
    }, 100)
  }

  hasLoaded.value = true
})

watch(formData, (newValue) => {
  if (hasLoaded.value && newValue) {
    // Normalize budget before saving to localStorage
    const normalized = {
      ...newValue,
      budget: normalizeBudget(newValue.budget)
    }
    localStorage.setItem('professionalDetails', JSON.stringify(normalized))
  }
}, { deep: true })

const goToNext = async () => {
  // Always normalize and save form data when clicking Next (even if validation fails)
  if (formData.value) {
    const normalized = {
      ...formData.value,
      budget: normalizeBudget(formData.value.budget)
    }
    localStorage.setItem('professionalDetails', JSON.stringify(normalized))
  }

  const result = professionalDetailsSchema.value.safeParse(formData.value)

  if (!result.success) {
    return
  }

  router.push('/expert/signup/experience')
}

const goToPrevious = () => {
  router.push('/expert/signup/contact-info')
}
</script>
